import * as React from "react";
import { useFormik } from "formik";
import { Link as RouterLink } from "react-router-dom";

import { Button, CircularProgress, makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import LOGO from "src/assets/svgs/logo.png";

import { requestAccountantPasswordReset } from "src/apiService/modules/accountant";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: { width: "100%", marginTop: theme.spacing(1) },
  submit: { margin: theme.spacing(3, 0, 2), padding: 12, position: "relative" },
  loading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: "-12px",
    marginLeft: "-12px",
  },
  back: { marginTop: theme.spacing(2), textAlign: "center" },
}));

export const AccountantForgotPasswordPage = () => {
  const classes = useStyles();
  const [sent, setSent] = React.useState(false);

  const formik = useFormik({
    initialValues: { email: "" },
    validate: (values) => {
      const errors: { email?: string } = {};
      if (!EMAIL_RE.test(values.email.trim()))
        errors.email = "Enter a valid email address.";
      return errors;
    },
    onSubmit: async (values, { setStatus }) => {
      try {
        await requestAccountantPasswordReset(values.email.trim());
        setSent(true);
      } catch (e) {
        setStatus({ error: (e as Error).message || "System error" });
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Box className="mb-5 h-36">
          <img src={LOGO} alt="My Reseller Genie" className="h-full" />
        </Box>
        <Typography component="h1" variant="h5">
          Reset your password
        </Typography>
        {sent ? (
          <Alert severity="success" style={{ marginTop: 16, width: "100%" }}>
            If an accountant account exists for that email, we&apos;ve sent a
            link to reset your password.
          </Alert>
        ) : (
          <form
            className={classes.form}
            noValidate
            onSubmit={formik.handleSubmit}
          >
            <Typography variant="body2" color="textSecondary" align="center">
              Enter your email and we&apos;ll send you a reset link.
            </Typography>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              type="email"
              autoFocus
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              error={formik.touched.email && !!formik.errors.email}
              helperText={
                formik.touched.email ? formik.errors.email : undefined
              }
              disabled={formik.isSubmitting}
            />
            {formik.status?.error && !formik.isSubmitting && (
              <Alert severity="error">{formik.status.error}</Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={formik.isSubmitting || !formik.isValid}
            >
              Send reset link
              {formik.isSubmitting && (
                <CircularProgress
                  className={classes.loading}
                  size={24}
                  color="inherit"
                />
              )}
            </Button>
          </form>
        )}
        <div className={classes.back}>
          <Link component={RouterLink} to="/accountant/login" variant="body2">
            Back to sign in
          </Link>
        </div>
      </div>
    </Container>
  );
};

export default AccountantForgotPasswordPage;
